// Tenant wallet (contract §9: default export; renders all four buckets — pending /
// available / reserved / lifetimePaid — AND a per-hold `releasesAt` line per hold; debt
// and frozen states visible). Replaces the old payout strip in
// `admin/payments/StripeTransactions.jsx`, which read the legacy
// `GET /tenant/payouts/summary` SHIM and showed one number — exactly the "where is my
// money" failure mode the gateway redesign exists to fix.
import React from "react";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { dateLocale } from "../../i18n/dateLocale";
import { fmtMoney } from "../../admin/money";
import { useTenantBalance } from "../hooks/useTenantBalance";
import { usePayoutRequest } from "../hooks/usePayoutRequest";
import { gatewayFailureText } from "../errorText";
import defaultGatewayApi from "../api";
import WithdrawalForm from "./WithdrawalForm";
import {
  Clock, Wallet, Lock, TrendingUp, Loader2, RefreshCw, AlertTriangle, ShieldOff,
} from "lucide-react";

const PAYOUT_STATE_LABEL_KEYS = { 
  reserving: "gw.state.reserving",
  requested: "gw.state.requested",
  approved: "gw.state.approved",
  transfer_created: "gw.state.transfer_created",
  transfer_unknown: "gw.state.transfer_unknown",
  payout_pending: "gw.state.payout_pending",
  paid: "gw.state.paid",
  payout_leg_failed: "gw.state.payout_leg_failed",
  failed: "gw.state.failed",
  rejected: "gw.state.rejected",
  abandoned: "gw.state.abandoned",
};
const PAYOUT_STATE_STYLES = {
  reserving: "bg-slate-100 text-slate-500",
  requested: "bg-amber-50 text-amber-700",
  approved: "bg-blue-50 text-blue-700",
  transfer_created: "bg-indigo-50 text-indigo-700",
  payout_pending: "bg-indigo-50 text-indigo-700",
  paid: "bg-emerald-50 text-emerald-700",
  payout_leg_failed: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700",
  rejected: "bg-rose-50 text-rose-700",
  abandoned: "bg-slate-100 text-slate-400",
};

function Bucket({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-xl border border-eh-border-soft bg-white px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-eh-muted mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-xl font-semibold text-eh-ink tabular-nums">{value}</div>
      {sub && <div className="text-xs text-eh-muted mt-0.5">{sub}</div>}
    </div>
  );
}

/** @param {object} [gatewayApi] DI seam (contract §9) — defaults to the module singleton
 *  @param {?string} [payoutSetupHref] where THIS mount's practitioner connects a payout
 *    destination, passed to the withdrawal form for its "not ready yet" note (BUG-222).
 *    Injected rather than hardcoded: the wallet is a gateway component and the surface
 *    that owns Stripe onboarding is an admin page, which is the direction the dependency
 *    already runs. Absent = no such surface here, and the note renders without a link. */
/** Basis points as a percentage, with no invented precision: 2000 -> "20", 1250 -> "12.5".
 *  Not in `admin/money.js` — that module formats amounts in a currency, and a rate is
 *  neither an amount nor in a currency. */
const fmtBps = (bps) => String(Math.round(Number(bps) / 10) / 10);

export default function GatewayWallet({ gatewayApi = defaultGatewayApi, payoutSetupHref = null }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { balance, loading, error, reload } = useTenantBalance(gatewayApi);
  const { payouts, loading: payoutsLoading, error: payoutsError, reload: reloadPayouts } =
    usePayoutRequest(gatewayApi);

  const refreshAll = React.useCallback(() => {
    reload();
    reloadPayouts();
  }, [reload, reloadPayouts]);

  // Poll for updates if any payout is in a non-terminal state, so asynchronous failures
  // or successes reflect in the history table without requiring a page reload.
  React.useEffect(() => {
    if (!payouts) return;
    const pendingStates = ["reserving", "requested", "approved", "transfer_created", "transfer_unknown", "payout_pending"];
    const hasPending = payouts.some(p => pendingStates.includes(p.status));
    
    if (hasPending) {
      const intervalId = setInterval(refreshAll, 5000);
      return () => clearInterval(intervalId);
    }
  }, [payouts, refreshAll]);

  if (loading && !balance) {
    return (
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6 flex items-center gap-2 text-sm text-eh-muted" data-testid="gateway-wallet-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("gw.wallet.loading")}
      </div>
    );
  }

  if (error && !balance) {
    return (
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid="gateway-wallet-error">
        <p className="text-sm text-rose-600 mb-3" role="alert">{t("gw.wallet.loadError")}</p>
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-eh-ink hover:underline"
          data-testid="gateway-wallet-retry"
        >
          <RefreshCw className="w-3.5 h-3.5" /> {t("gw.wallet.retry")}
        </button>
      </div>
    );
  }

  const currency = balance.currency;

  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6 space-y-5" data-testid="gateway-wallet">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl flex items-center gap-2">
          <Wallet className="w-5 h-5 text-sol-gold" /> {t("gw.wallet.title")}
        </h3>
        <button type="button" onClick={refreshAll} className="text-eh-muted hover:text-eh-ink" data-testid="gateway-wallet-refresh" title={t("gw.wallet.retry")}>
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {balance.frozen && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" role="alert" data-testid="gateway-wallet-frozen">
          <Lock className="w-4 h-4 flex-shrink-0" /> {t("gw.wallet.frozenBanner")}
        </div>
      )}
      {balance.debtCents > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="gateway-wallet-debt">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {t("gw.wallet.debtBanner", { amount: fmtMoney(balance.debtCents, currency) })}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="gateway-wallet-buckets">
        <Bucket icon={Clock} label={t("gw.wallet.bucket.pending")} value={fmtMoney(balance.pendingCents, currency)} testId="bucket-pending" />
        <Bucket icon={Wallet} label={t("gw.wallet.bucket.available")} value={fmtMoney(balance.availableCents, currency)} testId="bucket-available" />
        <Bucket icon={ShieldOff} label={t("gw.wallet.bucket.reserved")} value={fmtMoney(balance.reservedCents, currency)} testId="bucket-reserved" />
        <Bucket icon={TrendingUp} label={t("gw.wallet.bucket.lifetimePaid")} value={fmtMoney(balance.lifetimePaidCents, currency)} testId="bucket-lifetimePaid" />
      </div>

      {/* THE RATE THIS WALLET'S FIGURES WERE NET OF. Until 2026-08-28 the only surface
          stating the gateway's commission was the super-admin console, which a
          practitioner cannot open — while the Connect card showed them the legacy
          `platform_fees` rate captioned "per transaction". RENDERED ONLY WHEN SENT: the
          `limits` block is advisory and its stated rule is that a client with no number
          says nothing rather than presenting a constant to a practitioner as fact. */}
      {Number.isFinite(balance.limits?.commissionBps) && (
        <p className="text-xs text-eh-muted" data-testid="gateway-wallet-commission">
          {t("gw.wallet.commission", { pct: fmtBps(balance.limits.commissionBps) })}
        </p>
      )}

      {balance.adjustmentsCents !== 0 && (
        <p className="text-xs text-eh-muted" data-testid="gateway-wallet-adjustments">
          {t("gw.wallet.adjustments", { amount: fmtMoney(balance.adjustmentsCents, currency) })}
        </p>
      )}

      {balance.holds && balance.holds.length > 0 && (
        <div data-testid="gateway-wallet-holds">
          <div className="text-xs uppercase tracking-wide text-eh-muted mb-1.5">{t("gw.wallet.holdsTitle")}</div>
          <ul className="space-y-1">
            {balance.holds.map((h, i) => (
              <li key={i} className="flex justify-between text-sm" data-testid={`gateway-wallet-hold-${i}`}>
                <span className="text-eh-muted">
                  {t("gw.wallet.holdReleasesAt", { date: new Date(h.releasesAt).toLocaleDateString(dateLocale(lang)) })}
                </span>
                <span className="font-medium tabular-nums">{fmtMoney(h.amountCents, currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="pt-2 border-t border-eh-border-soft">
        <WithdrawalForm
          balance={balance}
          minPayoutCents={balance.limits?.minPayoutCents}
          payoutSetupHref={payoutSetupHref}
          onSubmitted={refreshAll}
          gatewayApi={gatewayApi}
        />
      </div>

      <div className="pt-2 border-t border-eh-border-soft">
        <div className="text-xs uppercase tracking-wide text-eh-muted mb-2">{t("gw.wallet.historyTitle")}</div>
        {payoutsLoading && !payouts && (
          <p className="text-sm text-eh-muted" data-testid="gateway-wallet-history-loading">{t("gw.wallet.loading")}</p>
        )}
        {payoutsError && !payouts && (
          <p className="text-sm text-rose-600" role="alert" data-testid="gateway-wallet-history-error">{t("gw.wallet.loadError")}</p>
        )}
        {payouts && payouts.length === 0 && (
          <p className="text-sm text-eh-muted text-center py-3" data-testid="gateway-wallet-history-empty">{t("gw.wallet.historyEmpty")}</p>
        )}
        {payouts && payouts.length > 0 && (
          <table className="w-full text-sm" data-testid="gateway-wallet-history-table">
            <thead>
              <tr className="text-left text-xs text-eh-muted border-b border-eh-border-soft">
                <th className="py-2 pr-4 font-medium">{t("gw.wallet.colAmount")}</th>
                <th className="py-2 pr-4 font-medium">{t("gw.wallet.colStatus")}</th>
                <th className="py-2 pr-4 font-medium">{t("gw.wallet.colRequested")}</th>
                <th className="py-2 font-medium">{t("gw.wallet.colFailure")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-eh-border-soft">
              {payouts.map((p) => (
                <tr key={p.payoutRequestId} data-testid={`payout-row-${p.payoutRequestId}`}>
                  <td className="py-2 pr-4 font-medium whitespace-nowrap">{fmtMoney(p.amountCents, p.currency)}</td>
                  <td className="py-2 pr-4">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${PAYOUT_STATE_STYLES[p.status] || "bg-slate-100 text-slate-600"}`}>
                      {t(PAYOUT_STATE_LABEL_KEYS[p.status] || "gw.state.unknown")}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-eh-muted whitespace-nowrap">
                    {p.requestedAt ? new Date(p.requestedAt).toLocaleDateString(dateLocale(lang)) : "—"}
                  </td>
                  <td className="py-2 text-rose-600">
                    {gatewayFailureText(p.failureCode, p.failureMessage, tc) || "—"}
                    {/* The one payout outcome the PRACTITIONER has to act on: the money
                        reached their Stripe balance and their own bank refused it, so
                        Solunary must not re-send it — §6 step 6, no retry edge. */}
                    {p.status === "payout_leg_failed" && (
                      <span
                        className="block mt-0.5 text-xs text-eh-muted"
                        data-testid={`payout-bank-leg-hint-${p.payoutRequestId}`}
                      >
                        {t("gw.wallet.bankLegFailedHint")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
